import { useState } from 'react'
import { Link } from 'react-router-dom'
import Recorder, { type RecorderResult } from '@/components/voice/Recorder'
import { formatMmSs } from '@/lib/voicesoap/duration'
import { toastSuccess } from '@/store/toast'

type Sample = {
  id: string
  label: string
  blob: Blob
  mimeType: string
  durationSec: number
  sizeBytes: number
  capturedAt: string
  url: string
}

/**
 * Audio sample capture page for Anubis eval case 10.
 *
 * Workflow (run on each physical device):
 *   1. Read the same Thai PT script aloud (see guidance below)
 *   2. Tap record → speak 60-120s → stop
 *   3. Label the sample (e.g. "ios-mp4-raw", "android-webm-opus")
 *   4. Download the blob → upload to shared drive / send to Anubis
 *
 * Three required samples:
 *   - Android Chrome: audio/webm;codecs=opus (native)
 *   - iOS Safari: audio/mp4 (native, raw)
 *   - iOS Safari → BE transcoded (BE receives mp4, ffmpeg → webm/opus;
 *     captured separately by downloading from server after upload)
 */
export default function DevAudioSamplesPage() {
  const [samples, setSamples] = useState<Sample[]>([])
  const [pendingLabel, setPendingLabel] = useState('')
  const [lastCapture, setLastCapture] = useState<RecorderResult | null>(null)

  function handleComplete(r: RecorderResult) {
    setLastCapture(r)
  }

  function handleSaveSample() {
    if (!lastCapture || !pendingLabel.trim()) return
    const url = URL.createObjectURL(lastCapture.blob)
    const sample: Sample = {
      id: `${Date.now()}`,
      label: pendingLabel.trim(),
      blob: lastCapture.blob,
      mimeType: lastCapture.mimeType,
      durationSec: lastCapture.durationSec,
      sizeBytes: lastCapture.blob.size,
      capturedAt: new Date().toISOString(),
      url,
    }
    setSamples((prev) => [sample, ...prev])
    setLastCapture(null)
    setPendingLabel('')
    toastSuccess('บันทึกตัวอย่างเสียงแล้ว — กดดาวน์โหลด')
  }

  function handleDiscard() {
    setLastCapture(null)
    setPendingLabel('')
  }

  function downloadFilename(s: Sample): string {
    const ext = mimeToExt(s.mimeType)
    const safe = s.label.replace(/[^a-z0-9_-]/gi, '_')
    return `${safe}_${s.durationSec.toFixed(0)}s.${ext}`
  }

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-5">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            🎙️ Audio Sample Capture — Anubis Case 10
          </h1>
          <p className="text-sm text-slate-500">
            เก็บตัวอย่างเสียง 3 แบบบนอุปกรณ์จริงเพื่อให้ Anubis ประเมิน ASR pipeline
          </p>
        </div>
        <Link to="/" className="text-sm text-slate-500 hover:text-brand-600">
          ← กลับ
        </Link>
      </header>

      <section className="card space-y-3 bg-amber-50 border-amber-200">
        <h2 className="font-semibold text-amber-900">คำแนะนำการอัด</h2>
        <ol className="list-decimal list-inside text-sm text-amber-900 space-y-1">
          <li>ใช้สคริปต์เดียวกันทั้ง 3 การอัด (ดูด้านล่าง) — พูดช้า ชัดเจน 60-120 วินาที</li>
          <li>
            อัดในที่เงียบ ห่างจากปากประมาณ 30 ซม — ใช้ไมค์ built-in ของเครื่อง
            (ไม่ใช่ bluetooth headset)
          </li>
          <li>Label ตัวอย่าง: <code>ios-mp4-raw</code>, <code>android-webm-opus</code>, หรือ <code>macos-safari-mp4</code></li>
          <li>หลัง &quot;บันทึกตัวอย่าง&quot; → กด <strong>ดาวน์โหลด</strong> → ส่งไฟล์ให้ Anubis</li>
        </ol>

        <details className="text-sm">
          <summary className="cursor-pointer font-medium text-amber-900">
            📝 สคริปต์แนะนำ (Thai PT follow-up visit)
          </summary>
          <blockquote className="mt-2 border-l-4 border-amber-400 bg-white p-3 rounded text-slate-700 leading-relaxed">
            ผู้ป่วยชื่อ สมศรี ใจดี HN 67001 มา follow-up ครั้งที่ 3
            ปวดหลังส่วนล่างด้านขวา VAS ลดจาก 6 เหลือ 3
            ยกของหนักได้ดีขึ้น นั่งทำงานได้นาน 45 นาที โดยไม่ปวด
            ROM lumbar flexion 70 องศา extension 20 องศา
            Special test SLR ขวา positive 70 องศา
            Intervention วันนี้ ultrasound 1 MHz 5 นาที บริเวณ L4-L5
            ตามด้วย mobilization grade 3
            Home exercise program pelvic tilt 10 ครั้ง 3 sets
            Follow-up อีก 1 สัปดาห์ ประเมินความแข็งแรง core stability
          </blockquote>
        </details>
      </section>

      <section className="card space-y-3">
        {!lastCapture ? (
          <Recorder onComplete={handleComplete} />
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
              ✅ อัดเสร็จแล้ว — {formatMmSs(lastCapture.durationSec)} ·{' '}
              <code>{lastCapture.mimeType}</code> ·{' '}
              {formatBytes(lastCapture.blob.size)}
            </div>
            <audio
              src={URL.createObjectURL(lastCapture.blob)}
              controls
              className="w-full"
            />
            <label className="block">
              <span className="block text-sm font-medium text-slate-700 mb-1">
                Label ตัวอย่าง (เช่น <code>ios-mp4-raw</code>)
              </span>
              <input
                type="text"
                className="input w-full"
                value={pendingLabel}
                onChange={(e) => setPendingLabel(e.target.value)}
                placeholder="device-mime-variant"
                autoFocus
              />
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSaveSample}
                disabled={!pendingLabel.trim()}
                className="btn-primary"
              >
                💾 บันทึกตัวอย่าง
              </button>
              <button
                type="button"
                onClick={handleDiscard}
                className="btn-secondary"
              >
                ทิ้งและอัดใหม่
              </button>
            </div>
          </div>
        )}
      </section>

      {samples.length > 0 && (
        <section className="card space-y-3">
          <h2 className="font-semibold text-slate-900">
            ตัวอย่างที่บันทึกไว้ในเซสชันนี้ ({samples.length})
          </h2>
          <ul className="space-y-3">
            {samples.map((s) => (
              <li
                key={s.id}
                className="rounded-md border border-slate-200 p-3 space-y-2"
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <div className="font-medium text-slate-900">{s.label}</div>
                    <div className="text-xs text-slate-500">
                      {formatMmSs(s.durationSec)} · <code>{s.mimeType}</code> ·{' '}
                      {formatBytes(s.sizeBytes)} ·{' '}
                      {new Date(s.capturedAt).toLocaleTimeString('th-TH')}
                    </div>
                  </div>
                  <a
                    href={s.url}
                    download={downloadFilename(s)}
                    className="btn-primary text-xs"
                  >
                    ⬇ ดาวน์โหลด
                  </a>
                </div>
                <audio src={s.url} controls className="w-full" />
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-500">
            ตัวอย่างเก็บใน memory เท่านั้น — refresh หน้าจะหาย กดดาวน์โหลดก่อนปิดหน้า
          </p>
        </section>
      )}
    </div>
  )
}

function mimeToExt(mime: string): string {
  if (mime.includes('webm')) return 'webm'
  if (mime.includes('mp4')) return 'm4a'
  if (mime.includes('ogg')) return 'ogg'
  if (mime.includes('wav')) return 'wav'
  return 'bin'
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(2)} MB`
}
